import { isDefined, isFunction, isNumber, } from '@showroomjs/core';
import { useMeasure } from '@showroomjs/measure';
import { Alert, useConstant, useId, Tooltip } from '@showroomjs/ui';
import * as React from 'react';
import allCompMetadata from 'react-showroom-all-components-docs';
import allImports from 'react-showroom-all-imports';
import CodeblockData from 'react-showroom-codeblocks';
import Wrapper from 'react-showroom-wrapper';
import { AllComponents } from '../all-components';
import { CodePreviewFrame } from '../components/code-preview-frame';
import { CodeImportsContextProvider } from '../lib/code-imports-context';
import { CodeVariablesContextProvider } from '../lib/code-variables-context';
import { ComponentMetaContext } from '../lib/component-props-context';
import { usePreviewWindow } from '../lib/frame-message';
import { Route, Routes, useParams } from '../lib/routing';
import { useA11yCheck } from '../lib/use-a11y-check';
import { UseCustomStateContext } from '../lib/use-custom-state';
import { useHeightChange } from '../lib/use-height-change';
import { useHighlights } from '../lib/use-highlights';
import { ConsoleContext } from '../lib/use-preview-console';
import { PropsEditorContext, serializePropsEditor, } from '../lib/use-props-editor';
const componentsMetas = Object.values(allCompMetadata);
export const PreviewApp = () => {
    return (React.createElement(Wrapper, null,
        React.createElement(Tooltip.Provider, null,
            React.createElement(CodeImportsContextProvider, { value: allImports },
                React.createElement(Routes, null,
                    React.createElement(Route, { path: "/:codeHash/:componentId", element: React.createElement(ComponentPreviewPage, null) }),
                    React.createElement(Route, { path: "/:codeHash", element: React.createElement(GenericPreviewPage, null) }),
                    React.createElement(Route, { path: "*", element: React.createElement(ErrorPage, null) }))))));
};
const defaultState = {
    code: '',
    lang: 'tsx',
};
const allCodeBlocks = CodeblockData.items.reduce((result, codeblock) => Object.assign({}, result, codeblock));
const ComponentPreviewPage = () => {
    const params = useParams();
    const metadata = React.useMemo(() => componentsMetas.find((m) => m.id === params.componentId), [params.componentId]);
    const variables = React.useMemo(() => {
        if (!metadata) {
            return {};
        }
        const Component = AllComponents[metadata.displayName];
        return Component
            ? {
                [metadata.displayName]: Component,
            }
            : {};
    }, [metadata]);
    const content = (React.createElement(CodeVariablesContextProvider, { value: variables },
        React.createElement(PreviewPage, null)));
    return metadata ? (React.createElement(ComponentMetaContext.Provider, { value: metadata }, content)) : (content);
};
const GenericPreviewPage = () => {
    return React.createElement(PreviewPage, null);
};
const PreviewPage = () => {
    const params = useParams();
    const codeData = React.useMemo(() => Object.entries(allCodeBlocks).find(([_, codeBlock]) => codeBlock && codeBlock.initialCodeHash === params.codeHash), [params.codeHash]);
    const [state, setState] = React.useState(codeData && codeData[1]
        ? {
            code: codeData[0],
            lang: codeData[1].lang,
        }
        : defaultState);
    const setStateMap = useConstant(() => new Map());
    const latestStateValueMap = useConstant(() => new Map());
    const customUseState = React.useMemo(() => createCustomUseState({
        onChange: (value, id) => sendParent({
            type: 'stateChange',
            stateValue: value,
            stateId: id,
        }),
        setStateMap,
        latestStateValueMap,
    }), []);
    const [measuring, setMeasuring] = React.useState(false);
    const isUpdatingRef = React.useRef(false);
    const isUpdatingTimerId = React.useRef(null);
    const isFiringEventRef = React.useRef(false);
    const isFiringEventTimerId = React.useRef(null);
    const [propsEditor, setPropsEditor] = React.useState(undefined);
    const rootRef = React.useRef(null);
    const highlightItems = useHighlights();
    const { sendParent } = usePreviewWindow((ev) => {
        if (ev.type === 'code') {
            setState(ev);
        }
        else if (ev.type === 'syncState') {
            if (process.env.SYNC_STATE_TYPE === 'state') {
                const setStateFn = setStateMap.get(ev.stateId);
                if (setStateFn) {
                    setStateFn(ev.stateValue);
                }
                latestStateValueMap.set(ev.stateId, ev.stateValue);
            }
        }
        else if (ev.type === 'scroll') {
            clearTimer(isUpdatingTimerId);
            const docEl = window.document.documentElement;
            if (docEl) {
                const [xPct, yPct] = ev.scrollPercentageXY;
                isUpdatingRef.current = true;
                if (isNumber(xPct)) {
                    window.document.documentElement.scrollLeft = Math.round(xPct * (docEl.scrollWidth - docEl.clientWidth));
                }
                if (isNumber(yPct)) {
                    window.document.documentElement.scrollTop = Math.round(yPct * (docEl.scrollHeight - docEl.clientHeight));
                }
                isUpdatingTimerId.current = window.setTimeout(() => {
                    isUpdatingRef.current = false;
                }, 500);
            }
        }
        else if (ev.type === 'domEvent') {
            if (process.env.SYNC_STATE_TYPE === 'event') {
                clearTimer(isFiringEventTimerId);
                isFiringEventRef.current = true;
                import(/* webpackPrefetch: true */ '../lib/fire-event').then((m) => m.fireValidDomEvent(ev.data));
                isFiringEventTimerId.current = window.setTimeout(() => {
                    isFiringEventRef.current = false;
                }, 500);
            }
        }
        else if (ev.type === 'syncPropsEditor') {
            setPropsEditor(ev.data);
        }
        else if (ev.type === 'toggleMeasure') {
            setMeasuring(ev.active);
        }
        else if (ev.type === 'highlightElements') {
            highlightItems(ev.selectors, ev.color);
        }
    });
    useA11yCheck(rootRef, (result) => sendParent({
        type: 'a11yCheckResult',
        result,
    }));
    React.useEffect(() => {
        function getHeight() {
            if (isUpdatingRef.current) {
                return;
            }
            const docEl = window.document.documentElement;
            const xTotal = docEl.scrollWidth - docEl.clientWidth;
            const yTotal = docEl.scrollHeight - docEl.clientHeight;
            sendParent({
                type: 'scroll',
                scrollPercentageXY: [
                    xTotal ? docEl.scrollLeft / xTotal : null,
                    yTotal ? docEl.scrollTop / yTotal : null,
                ],
            });
        }
        window.addEventListener('scroll', getHeight);
        return () => window.removeEventListener('scroll', getHeight);
    }, []);
    useHeightChange(typeof window === 'undefined' ? null : window.document.documentElement, (height) => sendParent({
        type: 'heightChange',
        height,
    }));
    const previewConsole = React.useMemo(() => {
        const addMessage = (level, ...msg) => {
            if (typeof window !== 'undefined') {
                sendParent({
                    type: 'log',
                    level,
                    data: msg,
                });
            }
        };
        return Object.assign({}, console, {
            log: addMessage.bind(null, 'log'),
            error: addMessage.bind(null, 'error'),
            fatal: addMessage.bind(null, 'fatal'),
            warn: addMessage.bind(null, 'warn'),
            info: addMessage.bind(null, 'info'),
        });
    }, [sendParent]);
    const createKeyboardEventHandler = (eventType) => (ev) => {
        if (process.env.SYNC_STATE_TYPE === 'state') {
            return;
        }
        if (isFiringEventRef.current) {
            return;
        }
        const domInfo = getDomEventInfo(ev);
        if (domInfo) {
            sendParent({
                type: 'domEvent',
                data: Object.assign(Object.assign({}, domInfo), { eventType, init: {
                        key: ev.key,
                        code: ev.code,
                        keyCode: ev.keyCode,
                        ctrlKey: ev.ctrlKey,
                        shiftKey: ev.shiftKey,
                        metaKey: ev.metaKey,
                    } }),
            });
        }
    };
    useMeasure({
        enabled: measuring,
    });
    return (React.createElement(UseCustomStateContext.Provider, { value: process.env.SYNC_STATE_TYPE === 'state'
            ? customUseState
            : React.useState },
        React.createElement(ConsoleContext.Provider, { value: previewConsole },
            React.createElement(PropsEditorContext.Provider, { value: [
                    propsEditor,
                    function onPropsEditorEvent(ev) {
                        if (ev.type === 'init') {
                            sendParent({
                                type: 'syncPropsEditor',
                                data: serializePropsEditor(ev.initialState),
                            });
                        }
                    },
                ] },
                React.createElement(CodePreviewFrame, Object.assign({}, state, { ref: rootRef, onIsCompilingChange: (isCompiling) => sendParent({
                        type: 'compileStatus',
                        isCompiling,
                    }), onChangeCapture: (ev) => {
                        if (process.env.SYNC_STATE_TYPE === 'state') {
                            return;
                        }
                        if (isFiringEventRef.current) {
                            return;
                        }
                        const domInfo = getDomEventInfo(ev);
                        if (domInfo) {
                            const { value, checked, type, files } = ev.target;
                            sendParent({
                                type: 'domEvent',
                                data: Object.assign(Object.assign({}, domInfo), { eventType: 'change', init: type === 'file'
                                        ? {
                                            target: {
                                                files: files && Array.from(files),
                                            },
                                        }
                                        : {
                                            target: {
                                                checked,
                                                value,
                                            },
                                        } }),
                            });
                        }
                    }, onClickCapture: (ev) => {
                        if (process.env.SYNC_STATE_TYPE === 'state') {
                            return;
                        }
                        if (isFiringEventRef.current) {
                            return;
                        }
                        const domInfo = getDomEventInfo(ev);
                        if (domInfo) {
                            if (domInfo.tag === 'label') {
                                // fire this will cause double click on the associated input
                                return;
                            }
                            if (domInfo.tag === 'input') {
                                const { type } = ev.target;
                                // fire this will cause all screen prompt for file selection.
                                if (type === 'file') {
                                    return;
                                }
                            }
                            sendParent({
                                type: 'domEvent',
                                data: Object.assign({ eventType: 'click' }, domInfo),
                            });
                        }
                    }, onKeyUpCapture: createKeyboardEventHandler('keyUp'), onKeyDownCapture: createKeyboardEventHandler('keyDown') }))))));
};
const observerInit = {
    rootMargin: '20% 0px',
};
const getDomEventInfo = (ev) => {
    const el = ev.target;
    const { tagName } = el;
    if (tagName) {
        const tag = tagName.toLowerCase();
        const allElements = Array.from(document.querySelectorAll(tag));
        return {
            tag,
            index: allElements.indexOf(el),
            tagTotal: allElements.length,
        };
    }
};
const clearTimer = (ref) => {
    const tValue = ref.current;
    if (isNumber(tValue)) {
        window.clearTimeout(tValue);
    }
};
const createCustomUseState = (options) => (initialState) => {
    const stateId = useId();
    const latestStateValue = options.latestStateValueMap.get(stateId);
    const initialValue = isDefined(latestStateValue)
        ? latestStateValue
        : initialState;
    const [state, setState] = React.useState(initialValue);
    const latestValueRef = React.useRef(state);
    latestValueRef.current = state;
    React.useEffect(() => {
        options.setStateMap.set(stateId, setState);
    }, [stateId]);
    const customSetState = React.useMemo(() => function customSetState(newState) {
        setState(newState);
        const nextState = isFunction(newState)
            ? newState(latestValueRef.current)
            : newState;
        options.onChange(nextState, stateId);
    }, []);
    return [state, customSetState];
};
const ErrorPage = () => {
    return (React.createElement("div", null,
        React.createElement(Alert, { variant: "error" },
            "Something goes wrong. This is probably a bug in",
            ' ',
            React.createElement("code", null, "react-showroom"),
            ".")));
};
//# sourceMappingURL=preview-app.js.map